import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { depositAPI, apiUtils } from '../api';
import Nav from './nav';
import { useNavigate } from 'react-router-dom';

const DepositPage = () => {
  const [step, setStep] = useState(1);
  const [selectedCoin, setSelectedCoin] = useState('USDT');
  const [amount, setAmount] = useState('');
  const [depositAddress, setDepositAddress] = useState('');
  const [copied, setCopied] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [depositHistory, setDepositHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showHistory, setShowHistory] = useState(false);
  const [availableBalance, setAvailableBalance] = useState(0);
  const [network, setNetwork] = useState('ERC20'); // Default network
  const navigate = useNavigate();

  const coins = [
    { 
      symbol: 'BTC', 
      name: 'Bitcoin', 
      image: '/images/coins/btc.jpg',
      networks: ['BTC'],
      minDeposit: 0.001
    },
    { 
      symbol: 'ETH', 
      name: 'Ethereum', 
      image: '/images/coins/eth.jpg',
      networks: ['ERC20'],
      minDeposit: 0.01
    },
    { 
      symbol: 'USDT', 
      name: 'Tether', 
      image: '/images/coins/usdt.png',
      networks: ['ERC20', 'TRC20', 'BEP20'],
      minDeposit: 10
    },
    { 
      symbol: 'BDX', 
      name: 'Beldex', 
      image: '/images/coins/bdx.png',
      networks: ['BDX'],
      minDeposit: 10
    },
    { 
      symbol: 'TRX', 
      name: 'Tron', 
      image: '/images/coins/trx.png',
      networks: ['TRC20'],
      minDeposit: 10
    },
    { 
      symbol: 'XRP', 
      name: 'Ripple', 
      image: '/images/coins/xrp.png',
      networks: ['XRP'],
      minDeposit: 10
    },
    { 
      symbol: 'DOGE', 
      name: 'Dogecoin', 
      image: '/images/coins/doge.png',
      networks: ['DOGE'],
      minDeposit: 10
    },
    { 
      symbol: 'BNB', 
      name: 'Binance Coin', 
      image: '/images/coins/bnb.jpg',
      networks: ['BEP20'],
      minDeposit: 0.1
    }
  ];

  const networkInfo = {
    'ERC20': { fee: '1-5 USDT', time: '2-5 minutes', color: 'from-blue-500 to-cyan-500' },
    'TRC20': { fee: '0-1 USDT', time: '1-3 minutes', color: 'from-red-500 to-pink-500' },
    'BEP20': { fee: '0.1-1 USDT', time: '1-3 minutes', color: 'from-yellow-500 to-orange-500' },
    'BTC': { fee: '0.0005 BTC', time: '10-30 minutes', color: 'from-orange-500 to-red-500' },
    'XRP': { fee: '0.1 XRP', time: '1-5 minutes', color: 'from-blue-600 to-purple-600' },
    'DOGE': { fee: '1 DOGE', time: '1-5 minutes', color: 'from-yellow-600 to-amber-600' },
    'BDX': { fee: '0.1 BDX', time: '1-5 minutes', color: 'from-purple-500 to-indigo-500' }
  };

  useEffect(() => {
    const token = localStorage.getItem('token');
    const userEmail = localStorage.getItem('userEmail');
    
    if (!token || !userEmail) {
      navigate('/login');
      return;
    }

    fetchDepositHistory();
    fetchBalance();
  }, [navigate]);

  const fetchDepositHistory = async () => {
    try {
      setLoading(true);
      const response = await depositAPI.getDepositHistory();
      setDepositHistory(response.data);
    } catch (error) {
      console.error('Error fetching deposit history:', error);
      const errorInfo = apiUtils.handleAPIError(error);
      setError(errorInfo.message);
    } finally {
      setLoading(false);
    }
  };

  const fetchBalance = async () => {
    try {
      const response = await axios.get('/api/user/balance');
      // setAvailableBalance(response.data.balance || 0);
    } catch (error) {
      // console.error('Error fetching balance:', error);
    }
  };

  const generateDepositAddress = async () => {
    setIsProcessing(true);
    setError('');
    try {
      const response = await depositAPI.generateAddress({
        currency: selectedCoin,
        network: network
      });
      setDepositAddress(response.data.address);
      setSuccess('Deposit address generated successfully!');
    } catch (error) {
      console.error('Error generating address:', error);
      const errorInfo = apiUtils.handleAPIError(error);
      setError(errorInfo.message);
    } finally {
      setIsProcessing(false);
    }
  };

  useEffect(() => {
    if (step === 2 && selectedCoin) {
      generateDepositAddress();
    }
  }, [step, selectedCoin, network]);

  const handleCoinSelect = (coin) => {
    setSelectedCoin(coin);
    const coinData = coins.find(c => c.symbol === coin);
    if (coinData && coinData.networks.length > 0) {
      setNetwork(coinData.networks[0]);
    }
  };

  const handleAmountChange = (e) => {
    const value = e.target.value;
    if (/^\d*\.?\d*$/.test(value)) {
      setAmount(value);
    }
  };

  const copyToClipboard = () => {
    if (depositAddress) {
      navigator.clipboard.writeText(depositAddress);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const handleConfirmDeposit = async () => {
    setIsProcessing(true);
    setError('');
    
    try {
      const selectedCoinData = coins.find(coin => coin.symbol === selectedCoin);
      
      if (parseFloat(amount) < selectedCoinData.minDeposit) {
        setError(`Minimum deposit amount for ${selectedCoin} is ${selectedCoinData.minDeposit}`);
        setIsProcessing(false);
        return;
      }

      const depositResponse = await depositAPI.createDeposit({
        amount: parseFloat(amount),
        currency: selectedCoin,
        cryptoAmount: parseFloat(amount),
        cryptoCurrency: selectedCoin,
        walletAddress: depositAddress,
        network: network
      });

      await depositAPI.confirmDeposit({
        depositId: depositResponse.data.deposit._id,
        transactionHash: `manual_${Date.now()}`
      });

      await fetchDepositHistory();
      // await fetchBalance();
      setStep(3);
      
    } catch (error) {
      console.error('Deposit failed:', error);
      const errorInfo = apiUtils.handleAPIError(error);
      setError(errorInfo.message);
    } finally {
      setIsProcessing(false);
    }
  };

  const quickAmounts = [50, 100, 200, 500, 1000, 2000];
  const selectedCoinData = coins.find(coin => coin.symbol === selectedCoin);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed': return 'text-green-400';
      case 'pending': return 'text-yellow-400';
      case 'failed': return 'text-red-400';
      default: return 'text-gray-400';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'completed': return 'fas fa-check-circle';
      case 'pending': return 'fas fa-clock';
      case 'failed': return 'fas fa-times-circle';
      default: return 'fas fa-question-circle';
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-gray-800 text-white pb-20">
      {/* Header */}
      <div className="bg-gray-800/50 backdrop-blur-lg border-b border-gray-700 sticky top-0 z-40">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <button 
              onClick={() => step > 1 ? setStep(step - 1) : navigate('/user/wallet')}
              className="p-2 hover:bg-gray-700/50 rounded-xl transition-all duration-200"
            >
              <i className="fas fa-arrow-left text-lg"></i>
            </button>
            <h1 className="text-xl font-bold bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
              {step === 1 && 'Deposit Funds'}
              {step === 2 && 'Deposit Details'}
              {step === 3 && 'Deposit Confirmed'}
            </h1>
            <button 
              onClick={() => setShowHistory(true)}
              className="p-2 hover:bg-gray-700/50 rounded-xl transition-all duration-200"
            >
              <i className="fas fa-history text-lg"></i>
            </button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-6 max-w-lg">
        {error && (
          <div className="bg-red-500/10 border border-red-500/20 rounded-2xl p-4 mb-6 backdrop-blur-sm">
            <div className="flex items-center gap-3 text-red-400">
              <i className="fas fa-exclamation-circle text-lg"></i>
              <span className="text-sm font-medium">{error}</span>
            </div>
          </div>
        )}

        {success && (
          <div className="bg-green-500/10 border border-green-500/20 rounded-2xl p-4 mb-6 backdrop-blur-sm">
            <div className="flex items-center gap-3 text-green-400">
              <i className="fas fa-check-circle text-lg"></i>
              <span className="text-sm font-medium">{success}</span>
            </div>
          </div>
        )}

        {step === 1 && (
          <div className="space-y-6">
            {/* Balance Card */}
            {/* <div className="bg-gradient-to-br from-blue-600/20 to-purple-600/20 border border-gray-700/50 rounded-2xl p-6 text-center backdrop-blur-sm">
              <div className="text-blue-100 text-sm mb-2">Available Balance</div>
              <div className="text-3xl font-bold mb-1 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                ${availableBalance.toLocaleString()}
              </div>
              <div className="text-blue-200 text-sm">Ready to deposit</div>
            </div> */}

            {/* Coin Selection */}
            <div className="bg-gray-800/50 border border-gray-700/50 rounded-2xl p-6 backdrop-blur-sm">
              <h2 className="text-lg font-semibold mb-4">Select Currency</h2>
              <div className="grid grid-cols-4 gap-3">
                {coins.map((coin) => (
                  <button
                    key={coin.symbol}
                    onClick={() => handleCoinSelect(coin.symbol)}
                    className={`flex flex-col items-center p-3 rounded-xl border-2 transition-all duration-200 ${
                      selectedCoin === coin.symbol
                        ? 'border-blue-500 bg-blue-500/20 shadow-lg shadow-blue-500/25'
                        : 'border-gray-600 hover:border-gray-500 hover:bg-gray-700/50'
                    }`}
                  >
                    <img 
                      src={coin.image} 
                      alt={coin.name}
                      className="w-10 h-10 rounded-full mb-2"
                      onError={(e) => {
                        e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiByeD0iMjAiIGZpbGw9IiMzNzM3NDAiLz4KPC9zdmc+';
                      }}
                    />
                    <span className="text-xs font-medium">{coin.symbol}</span>
                  </button>
                ))}
              </div>
            </div>

            {/* Network Selection */}
            {selectedCoinData && selectedCoinData.networks.length > 1 && (
              <div className="bg-gray-800/50 border border-gray-700/50 rounded-2xl p-6 backdrop-blur-sm">
                <h2 className="text-lg font-semibold mb-4">Select Network</h2>
                <div className="grid grid-cols-2 gap-3">
                  {selectedCoinData.networks.map((net) => (
                    <button
                      key={net}
                      onClick={() => setNetwork(net)}
                      className={`p-4 rounded-xl border-2 transition-all duration-200 text-left ${
                        network === net
                          ? `border-blue-500 bg-gradient-to-r ${networkInfo[net]?.color || 'from-blue-500 to-purple-500'}/20 shadow-lg shadow-blue-500/25`
                          : 'border-gray-600 hover:border-gray-500 hover:bg-gray-700/50'
                      }`}
                    >
                      <div className="font-semibold text-sm">{net}</div>
                      <div className="text-xs text-gray-400 mt-1">
                        Fee: {networkInfo[net]?.fee || 'Varies'}
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Amount Input */}
            <div className="bg-gray-800/50 border border-gray-700/50 rounded-2xl p-6 backdrop-blur-sm">
              <div className="flex justify-between items-center mb-4">
                <label className="text-lg font-semibold">Amount ({selectedCoin})</label>
                <button 
                  onClick={() => setAmount(availableBalance.toString())}
                  className="text-blue-400 hover:text-blue-300 text-sm font-medium transition-colors"
                >
                  Max
                </button>
              </div>
              
              <div className="relative">
                <input
                  type="text"
                  value={amount}
                  onChange={handleAmountChange}
                  className="w-full bg-gray-700/50 border-2 border-gray-600 rounded-xl py-4 px-4 text-2xl font-bold focus:outline-none focus:border-blue-500 transition-colors backdrop-blur-sm"
                  placeholder="0.00"
                />
                {amount && (
                  <button 
                    onClick={() => setAmount('')}
                    className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                )}
              </div>

              {/* Quick Amounts */}
              <div className="mt-4">
                <div className="text-sm text-gray-400 mb-2">Quick Select</div>
                <div className="grid grid-cols-3 gap-2">
                  {quickAmounts.map((quickAmount) => (
                    <button
                      key={quickAmount}
                      onClick={() => setAmount(quickAmount.toString())}
                      className="py-3 bg-gray-700/50 hover:bg-gray-700 rounded-lg transition-all duration-200 text-sm font-medium backdrop-blur-sm"
                    >
                      ${quickAmount}
                    </button>
                  ))}
                </div>
              </div>

              {selectedCoinData && (
                <div className="mt-4 text-xs text-gray-400 text-center">
                  Minimum deposit: {selectedCoinData.minDeposit} {selectedCoin}
                </div>
              )}
            </div>

            {/* Network Info */}
            {network && networkInfo[network] && (
              <div className="bg-blue-500/10 border border-blue-500/20 rounded-2xl p-4">
                <div className="flex items-start gap-3">
                  <i className="fas fa-info-circle text-blue-400 mt-1"></i>
                  <div className="text-sm text-blue-300">
                    <p className="font-semibold">{network} Network</p>
                    <p className="mt-1">Fee: {networkInfo[network].fee} • Time: {networkInfo[network].time}</p>
                  </div>
                </div>
              </div>
            )}

            {/* Continue Button */}
            <button
              onClick={() => setStep(2)}
              disabled={!amount || parseFloat(amount) <= 0}
              className="w-full bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 disabled:from-gray-600 disabled:to-gray-700 disabled:cursor-not-allowed text-white font-bold py-4 px-6 rounded-xl transition-all duration-200 shadow-lg shadow-blue-500/25"
            >
              Continue to Deposit
            </button>
          </div>
        )}

        {step === 2 && (
          <div className="space-y-6">
            {/* Selected Coin Info */}
            <div className="bg-gradient-to-br from-blue-600/20 to-purple-600/20 border border-gray-700/50 rounded-2xl p-6 text-center backdrop-blur-sm">
              <div className="flex items-center justify-center gap-4 mb-4">
                <img 
                  src={selectedCoinData?.image} 
                  alt={selectedCoinData?.name}
                  className="w-16 h-16 rounded-full"
                  onError={(e) => {
                    e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj4KPHJlY3Qgd2lkdGg9IjQwIiBoZWlnaHQ9IjQwIiByeD0iMjAiIGZpbGw9IiMzNzM3NDAiLz4KPC9zdmc+';
                  }}
                />
                <div className="text-left">
                  <h2 className="text-xl font-bold">{selectedCoinData?.name}</h2>
                  <p className="text-gray-400">{selectedCoin} • {network}</p>
                </div>
              </div>
              
              {isProcessing ? (
                <div className="py-8">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500 mx-auto mb-4"></div>
                  <p className="text-gray-400">Generating deposit address...</p>
                </div>
              ) : depositAddress ? (
                <>
                  <div className="bg-white p-4 rounded-2xl inline-block max-w-[200px] shadow-lg">
                    <img
                      src={`https://api.qrserver.com/v1/create-qr-code/?data=${depositAddress}&size=200x200&margin=10`}
                      alt="Deposit QR Code"
                      className="w-full h-auto rounded-lg"
                    />
                  </div>
                  <p className="text-sm text-gray-400 mt-4">Scan QR code or copy address below</p>
                </>
              ) : (
                <div className="py-8">
                  <p className="text-gray-400">Failed to generate address</p>
                  <button
                    onClick={generateDepositAddress}
                    className="mt-3 bg-blue-500 hover:bg-blue-600 text-white px-4 py-2 rounded-lg transition-colors"
                  >
                    Retry
                  </button>
                </div>
              )}
            </div>

            {/* Deposit Details */}
            <div className="bg-gray-800/50 border border-gray-700/50 rounded-2xl p-6 backdrop-blur-sm">
              <div className="space-y-4">
                <div className="flex justify-between items-center pb-4 border-b border-gray-600">
                  <span className="text-gray-400">Network</span>
                  <span className="font-semibold">{network}</span>
                </div>

                <div>
                  <div className="flex items-center justify-between mb-3">
                    <span className="text-gray-400">Deposit Address</span>
                    <button
                      onClick={copyToClipboard}
                      disabled={!depositAddress}
                      className={`flex items-center gap-2 text-sm transition-colors ${
                        depositAddress 
                          ? 'text-blue-400 hover:text-blue-300' 
                          : 'text-gray-500 cursor-not-allowed'
                      }`}
                    >
                      {copied ? (
                        <>
                          <i className="fas fa-check text-green-400"></i>
                          <span className="text-green-400">Copied!</span>
                        </>
                      ) : (
                        <>
                          <i className="fas fa-copy"></i>
                          <span>Copy</span>
                        </>
                      )}
                    </button>
                  </div>
                  <div className="bg-gray-700/50 rounded-xl p-4 backdrop-blur-sm">
                    <p className="text-sm font-mono break-all text-center">
                      {depositAddress || 'Generating address...'}
                    </p>
                  </div>
                </div>

                <div className="bg-yellow-500/10 border border-yellow-500/20 rounded-xl p-4">
                  <div className="flex items-start gap-3">
                    <i className="fas fa-exclamation-triangle text-yellow-400 mt-1"></i>
                    <div className="text-sm text-yellow-300">
                      <p className="font-semibold">Important Notice</p>
                      <p className="mt-1">
                        Only send {selectedCoin} ({network} network) to this address. 
                        Sending other assets or using wrong network may result in permanent loss.
                      </p>
                    </div>
                  </div>
                </div>

                {selectedCoinData && (
                  <div className="bg-blue-500/10 border border-blue-500/20 rounded-xl p-4">
                    <div className="flex items-start gap-3">
                      <i className="fas fa-clock text-blue-400 mt-1"></i>
                      <div className="text-sm text-blue-300">
                        <p className="font-semibold">Processing Time</p>
                        <p className="mt-1">
                          Deposits require {networkInfo[network]?.time || '2-5 minutes'} for confirmation. 
                          Minimum deposit: {selectedCoinData.minDeposit} {selectedCoin}
                        </p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>

            <button
              onClick={handleConfirmDeposit}
              disabled={isProcessing || !depositAddress}
              className="w-full bg-gradient-to-r from-green-500 to-emerald-600 hover:from-green-600 hover:to-emerald-700 disabled:from-gray-600 disabled:to-gray-700 text-white font-bold py-4 px-6 rounded-xl transition-all duration-200 shadow-lg shadow-green-500/25"
            >
              {isProcessing ? (
                <div className="flex items-center justify-center gap-2">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Processing...
                </div>
              ) : (
                'I Have Sent the Funds'
              )}
            </button>
          </div>
        )}

        {step === 3 && (
          <div className="text-center space-y-6">
            {/* Success Card */}
            <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-2xl p-8 backdrop-blur-sm">
              <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-500 rounded-full flex items-center justify-center mx-auto mb-4 shadow-lg shadow-green-500/25">
                <i className="fas fa-check text-white text-2xl"></i>
              </div>
              <h2 className="text-2xl font-bold mb-2 bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                Deposit Confirmed!
              </h2>
              <p className="text-gray-400">
                Your deposit of <span className="text-white font-semibold">{amount} {selectedCoin}</span> is being processed.
              </p>
            </div>

            {/* Transaction Details */}
            <div className="bg-gray-800/50 border border-gray-700/50 rounded-2xl p-6 backdrop-blur-sm">
              <h3 className="text-lg font-semibold mb-4 text-left">Transaction Details</h3>
              <div className="space-y-4 text-sm text-left">
                <div className="flex justify-between">
                  <span className="text-gray-400">Amount</span>
                  <span className="font-semibold">{amount} {selectedCoin}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Network</span>
                  <span className="font-semibold">{network}</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Status</span>
                  <span className="text-green-400 font-semibold flex items-center gap-2">
                    <i className="fas fa-check-circle"></i>
                    Processing
                  </span>
                </div>
                <div className="flex justify-between">
                  <span className="text-gray-400">Estimated Time</span>
                  <span className="font-semibold">{networkInfo[network]?.time || '2-5 minutes'}</span>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="space-y-3">
              <button
                onClick={() => {
                  setStep(1);
                  setAmount('');
                  setDepositAddress('');
                  setError('');
                  setSuccess('');
                }}
                className="w-full bg-gradient-to-r from-blue-500 to-cyan-600 hover:from-blue-600 hover:to-cyan-700 text-white font-bold py-4 px-6 rounded-xl transition-all duration-200 shadow-lg shadow-blue-500/25"
              >
                Make Another Deposit
              </button>
              <button
                onClick={() => navigate('/user/wallet')}
                className="w-full bg-gray-700/50 hover:bg-gray-700 border border-gray-600 text-white font-bold py-4 px-6 rounded-xl transition-all duration-200 backdrop-blur-sm"
              >
                View Wallet
              </button>
            </div>
          </div>
        )}
      </div>

      {/* History Modal */}
      {showHistory && (
        <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-end justify-center z-50 px-4 pb-4 sm:items-center sm:p-4">
          <div className="bg-gray-800 border border-gray-700 rounded-2xl w-full max-w-md max-h-[80vh] overflow-hidden animate-slide-up">
            <div className="sticky top-0 bg-gray-800 border-b border-gray-700 p-6">
              <div className="flex items-center justify-between">
                <h3 className="text-xl font-bold">Deposit History</h3>
                <button
                  onClick={() => setShowHistory(false)}
                  className="p-2 hover:bg-gray-700/50 rounded-xl transition-all duration-200"
                >
                  <i className="fas fa-times text-lg"></i>
                </button>
              </div>
            </div>
            
            <div className="p-6 overflow-y-auto max-h-[60vh]">
              {loading ? (
                <div className="flex justify-center py-8">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                </div>
              ) : depositHistory.length > 0 ? (
                <div className="space-y-3">
                  {depositHistory.map((deposit) => (
                    <div key={deposit._id} className="flex items-center justify-between p-4 bg-gray-700/30 rounded-xl backdrop-blur-sm">
                      <div className="flex items-center space-x-3 flex-1">
                        <i className={`${getStatusIcon(deposit.status)} ${getStatusColor(deposit.status)} text-lg`}></i>
                        <div className="flex-1 min-w-0">
                          <p className="font-semibold truncate">{deposit.amount} {deposit.currency}</p>
                          <p className="text-xs text-gray-400 truncate">
                            {formatDate(deposit.createdAt)} • {deposit.network || 'Main'}
                          </p>
                        </div>
                      </div>
                      <span className={`text-sm font-medium ${getStatusColor(deposit.status)}`}>
                        {deposit.status.charAt(0).toUpperCase() + deposit.status.slice(1)}
                      </span>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-400">
                  <i className="fas fa-history text-3xl mb-3"></i>
                  <p>No deposit history found</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Navigation */}
      <Nav />

      {/* Include Font Awesome */}
      <link rel="stylesheet" href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/6.0.0-beta3/css/all.min.css" />
      
      <style jsx>{`
        @keyframes slide-up {
          from { transform: translateY(100%); }
          to { transform: translateY(0); }
        }
        .animate-slide-up {
          animation: slide-up 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default DepositPage;